import React, { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from '../../services/supabaseClient';

/**
 * LocationCascadeSelector Component
 * Cascading dropdown location selector: System → Planet → Location
 *
 * @param {Object} props
 * @param {Function} props.onChange - Callback when complete location is selected (receives location object)
 * @param {Object} props.value - Current selected location (optional)
 * @param {String} props.className - Additional CSS classes
 * @param {Boolean} props.required - Whether all fields are required
 * @param {Boolean} props.tradingOnly - Only show locations with has_commodities or has_merchants (default: false)
 * @param {Boolean} props.discoverableOnly - Only show discoverable locations (default: true)
 * @param {Array} props.specialTags - Filter by special tags (optional)
 */
const LocationCascadeSelector = ({
  onChange,
  value,
  className = '',
  required = false,
  tradingOnly = false,
  discoverableOnly = true,
  specialTags = null
}) => {
  const [systems, setSystems] = useState([]);
  const [planets, setPlanets] = useState([]);
  const [locations, setLocations] = useState([]);

  const [selectedSystem, setSelectedSystem] = useState('');
  const [selectedPlanet, setSelectedPlanet] = useState('');
  const [selectedLocation, setSelectedLocation] = useState('');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Track if we're initializing from a pre-populated value to prevent cascading resets
  // Using ref instead of state to avoid re-triggering useEffects
  const isInitializingRef = useRef(false);
  const initialValueRef = useRef(null);
  const hasInitializedRef = useRef(false);

  // Helper to build query with common filters
  const applyCommonFilters = useCallback((query) => {
    if (discoverableOnly) {
      query = query.eq('discoverable', true);
    }
    if (tradingOnly) {
      query = query.or('has_commodities.eq.true,has_merchants.eq.true');
    }
    if (specialTags && specialTags.length > 0) {
      query = query.overlaps('special_tags', specialTags);
    }
    return query;
  }, [discoverableOnly, tradingOnly, specialTags]);

  // Initialize from value prop - runs complete initialization sequence
  useEffect(() => {
    if (value && value.system && !hasInitializedRef.current) {
      console.log('[LocationCascadeSelector] Initializing with value:', value);
      hasInitializedRef.current = true;
      isInitializingRef.current = true;
      initialValueRef.current = {
        system: value.system,
        planet: value.planet,
        id: value.id
      };

      // Run the complete initialization sequence
      const initializeFromValue = async () => {
        try {
          setLoading(true);

          // Step 1: Set system immediately
          setSelectedSystem(value.system);

          // Step 2: Fetch and set planets for this system
          let planetQuery = supabase
            .from('game_locations')
            .select('planet')
            .eq('system', value.system)
            .not('planet', 'is', null);
          planetQuery = applyCommonFilters(planetQuery);
          planetQuery = planetQuery.order('planet');

          const { data: planetData, error: planetError } = await planetQuery;
          if (planetError) throw planetError;

          const uniquePlanets = [...new Set(planetData.map(loc => loc.planet))].filter(Boolean);
          setPlanets(uniquePlanets);
          console.log('[LocationCascadeSelector] Loaded planets:', uniquePlanets);

          // Step 3: Set planet if provided
          if (value.planet && uniquePlanets.includes(value.planet)) {
            setSelectedPlanet(value.planet);
            console.log('[LocationCascadeSelector] Setting planet:', value.planet);

            // Step 4: Fetch locations for this planet
            let locationQuery = supabase
              .from('game_locations')
              .select('*')
              .eq('system', value.system)
              .eq('planet', value.planet);
            locationQuery = applyCommonFilters(locationQuery);
            locationQuery = locationQuery.order('name');

            const { data: locationData, error: locationError } = await locationQuery;
            if (locationError) throw locationError;

            setLocations(locationData || []);
            console.log('[LocationCascadeSelector] Loaded locations:', locationData?.length);

            // Step 5: Set location if provided and exists in the list
            if (value.id) {
              const locationExists = locationData?.some(loc => loc.id === value.id);
              if (locationExists) {
                setSelectedLocation(value.id);
                console.log('[LocationCascadeSelector] Setting location:', value.id);
              } else {
                console.warn('[LocationCascadeSelector] Location ID not found in list:', value.id);
              }
            }
          } else if (value.planet) {
            console.warn('[LocationCascadeSelector] Planet not found in system:', value.planet);
          }
        } catch (err) {
          console.error('[LocationCascadeSelector] Initialization error:', err);
          setError('Failed to initialize location selector');
        } finally {
          setLoading(false);
          isInitializingRef.current = false;
          initialValueRef.current = null;
        }
      };

      initializeFromValue();
    }
  }, [value, applyCommonFilters]);

  // Fetch all systems on mount
  useEffect(() => {
    fetchSystems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch planets when system changes (user interaction only)
  useEffect(() => {
    // Skip if we're in the middle of initialization
    if (isInitializingRef.current) {
      return;
    }

    if (selectedSystem) {
      fetchPlanets(selectedSystem);
      // Reset downstream selections on user system change
      setSelectedPlanet('');
      setSelectedLocation('');
      setLocations([]);
    } else {
      setPlanets([]);
      setLocations([]);
      setSelectedPlanet('');
      setSelectedLocation('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSystem]);

  // Fetch locations when planet changes (user interaction only)
  useEffect(() => {
    // Skip if we're in the middle of initialization
    if (isInitializingRef.current) {
      return;
    }

    if (selectedSystem && selectedPlanet) {
      fetchLocations(selectedSystem, selectedPlanet);
      // Reset location on user planet change
      setSelectedLocation('');
    } else if (selectedSystem && !selectedPlanet) {
      // Fetch system-level locations (like space stations not tied to planets)
      fetchSystemLocations(selectedSystem);
      setSelectedLocation('');
    } else {
      setLocations([]);
      setSelectedLocation('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSystem, selectedPlanet]);

  // Notify parent when location is selected
  useEffect(() => {
    // Skip during initialization to prevent clearing parent state
    if (isInitializingRef.current) {
      return;
    }

    if (selectedLocation && locations.length > 0) {
      const location = locations.find(loc => loc.id === selectedLocation);
      if (location) {
        onChange(location);
      }
    } else if (!selectedLocation) {
      // Don't call onChange(null) if we have a value prop with system set
      // This prevents clearing the parent's location state during initialization
      if (!value || !value.system) {
        onChange(null);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLocation, locations]);

  const fetchSystems = async () => {
    setLoading(true);
    setError(null);
    try {
      let query = supabase
        .from('game_locations')
        .select('system')
        .not('system', 'is', null);

      // Apply discoverable filter
      if (discoverableOnly) {
        query = query.eq('discoverable', true);
      }

      // Apply trading filter
      if (tradingOnly) {
        query = query.or('has_commodities.eq.true,has_merchants.eq.true');
      }

      // Apply special tags filter
      if (specialTags && specialTags.length > 0) {
        query = query.overlaps('special_tags', specialTags);
      }

      query = query.order('system');

      const { data, error: queryError } = await query;

      if (queryError) throw queryError;

      // Get unique systems
      const uniqueSystems = [...new Set(data.map(loc => loc.system))].filter(Boolean);
      setSystems(uniqueSystems);
    } catch (err) {
      console.error('Error fetching systems:', err);
      setError('Failed to load systems. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchPlanets = async (system) => {
    setLoading(true);
    setError(null);
    try {
      let query = supabase
        .from('game_locations')
        .select('planet')
        .eq('system', system)
        .not('planet', 'is', null);

      // Apply discoverable filter
      if (discoverableOnly) {
        query = query.eq('discoverable', true);
      }

      // Apply trading filter
      if (tradingOnly) {
        query = query.or('has_commodities.eq.true,has_merchants.eq.true');
      }

      // Apply special tags filter
      if (specialTags && specialTags.length > 0) {
        query = query.overlaps('special_tags', specialTags);
      }

      query = query.order('planet');

      const { data, error: queryError } = await query;

      if (queryError) throw queryError;

      // Get unique planets
      const uniquePlanets = [...new Set(data.map(loc => loc.planet))].filter(Boolean);
      setPlanets(uniquePlanets);
    } catch (err) {
      console.error('Error fetching planets:', err);
      setError('Failed to load planets. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchSystemLocations = async (system) => {
    setLoading(true);
    setError(null);
    try {
      let query = supabase
        .from('game_locations')
        .select('*')
        .eq('system', system)
        .is('planet', null);

      // Apply discoverable filter
      if (discoverableOnly) {
        query = query.eq('discoverable', true);
      }

      // Apply trading filter
      if (tradingOnly) {
        query = query.or('has_commodities.eq.true,has_merchants.eq.true');
      }

      // Apply special tags filter
      if (specialTags && specialTags.length > 0) {
        query = query.overlaps('special_tags', specialTags);
      }

      query = query.order('name');

      const { data, error: queryError } = await query;

      if (queryError) throw queryError;

      setLocations(data || []);
    } catch (err) {
      console.error('Error fetching system locations:', err);
      setError('Failed to load locations. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchLocations = async (system, planet) => {
    setLoading(true);
    setError(null);
    try {
      let query = supabase
        .from('game_locations')
        .select('*')
        .eq('system', system)
        .eq('planet', planet);

      // Apply discoverable filter
      if (discoverableOnly) {
        query = query.eq('discoverable', true);
      }

      // Apply trading filter
      if (tradingOnly) {
        query = query.or('has_commodities.eq.true,has_merchants.eq.true');
      }

      // Apply special tags filter
      if (specialTags && specialTags.length > 0) {
        query = query.overlaps('special_tags', specialTags);
      }

      query = query.order('name');

      const { data, error: queryError } = await query;

      if (queryError) throw queryError;

      setLocations(data || []);
    } catch (err) {
      console.error('Error fetching locations:', err);
      setError('Failed to load locations. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getLocationLabel = (location) => {
    // Prioritize: name > station > moon > category
    if (location.name) return location.name;
    if (location.station) return location.station;
    if (location.moon) return location.moon;
    return location.category || 'Unknown';
  };

  const getLocationTypeBadge = (locationType) => {
    const typeStyles = {
      'system': { color: '#9370DB', label: 'System' },
      'planet': { color: '#4169E1', label: 'Planet' },
      'moon': { color: '#87CEEB', label: 'Moon' },
      'station': { color: '#FFD700', label: 'Station' },
      'city': { color: '#FF8C00', label: 'City' },
      'outpost': { color: '#A0522D', label: 'Outpost' },
      'endpoint': { color: '#00E6E6', label: 'Location' }
    };

    const style = typeStyles[locationType] || typeStyles['endpoint'];

    return (
      <span
        style={{
          background: `${style.color}22`,
          color: style.color,
          border: `1px solid ${style.color}`,
          padding: '2px 6px',
          borderRadius: 3,
          fontSize: '0.7rem',
          fontWeight: 600,
          marginLeft: 6,
          textTransform: 'uppercase'
        }}
      >
        {style.label}
      </span>
    );
  };

  const getSecurityBadge = (securityLevel) => {
    const securityStyles = {
      'high': { color: '#00FF00', icon: '✓' },
      'medium': { color: '#FFD447', icon: '!' },
      'low': { color: '#FF8C00', icon: '⚠' },
      'lawless': { color: '#FF4444', icon: '☠' }
    };

    const style = securityStyles[securityLevel] || securityStyles['medium'];

    return (
      <span
        style={{
          color: style.color,
          fontSize: '0.8rem',
          marginLeft: 6
        }}
        title={`Security: ${securityLevel}`}
      >
        {style.icon}
      </span>
    );
  };

  const getFeatureIcons = (location) => {
    const features = [];

    if (location.has_commodities) {
      features.push(
        <span key="commodities" title="Commodity Trading Available" style={{ color: '#FFD447', marginLeft: 4 }}>
          📦
        </span>
      );
    }

    if (location.has_merchants) {
      features.push(
        <span key="merchants" title="Merchants Available" style={{ color: '#00E6E6', marginLeft: 4 }}>
          🏪
        </span>
      );
    }

    if (location.has_loot) {
      features.push(
        <span key="loot" title="Loot Locations" style={{ color: '#9370DB', marginLeft: 4 }}>
          💎
        </span>
      );
    }

    if (location.has_missions) {
      features.push(
        <span key="missions" title="Mission Giver Available" style={{ color: '#00FF00', marginLeft: 4 }}>
          📋
        </span>
      );
    }

    return features;
  };

  const getLocationSubtext = (location) => {
    const parts = [];

    // Add location type
    if (location.location_type && location.location_type !== 'endpoint') {
      parts.push(location.location_type.replace('_', ' '));
    }

    // Add category if different from location type
    if (location.category && location.category !== location.location_type) {
      parts.push(location.category.replace('_', ' '));
    }

    // Add moon reference
    if (location.moon) {
      parts.push(`Moon: ${location.moon}`);
    }

    // Add station reference (if not already the name)
    if (location.station && location.station !== location.name) {
      parts.push(location.station);
    }

    // Add hierarchy level indicator
    if (location.hierarchy_level) {
      const levels = {
        1: 'Tier 1',
        2: 'Tier 2',
        3: 'Tier 3',
        4: 'Tier 4'
      };
      parts.push(levels[location.hierarchy_level] || `Level ${location.hierarchy_level}`);
    }

    return parts.join(' • ');
  };

  return (
    <div className={`location-cascade-selector ${className}`}>
      {/* Error Display */}
      {error && (
        <div className="alert alert-danger" style={{ fontSize: '0.9rem', marginBottom: '1rem' }}>
          <strong>Error:</strong> {error}
        </div>
      )}

      {/* Filter Info Badge */}
      {(tradingOnly || !discoverableOnly || (specialTags && specialTags.length > 0)) && (
        <div className="mb-3" style={{ fontSize: '0.85rem', color: '#00E6E6' }}>
          <strong>Active Filters:</strong>
          {tradingOnly && <span className="ms-2 badge" style={{ background: '#FFD44722', color: '#FFD447', border: '1px solid #FFD447' }}>Trading Locations Only</span>}
          {!discoverableOnly && <span className="ms-2 badge" style={{ background: '#9370DB22', color: '#9370DB', border: '1px solid #9370DB' }}>All Locations</span>}
          {specialTags && specialTags.length > 0 && (
            <span className="ms-2 badge" style={{ background: '#00E6E622', color: '#00E6E6', border: '1px solid #00E6E6' }}>
              {specialTags.join(', ')}
            </span>
          )}
        </div>
      )}

      {/* System Dropdown */}
      <div className="mb-3">
        <label className="form-label" style={{ color: '#00E6E6', fontWeight: 600 }}>
          System {required && <span style={{ color: '#ff4444' }}>*</span>}
        </label>
        <select
          className="form-select"
          value={selectedSystem}
          onChange={(e) => setSelectedSystem(e.target.value)}
          disabled={loading || systems.length === 0}
          required={required}
          style={{
            background: '#23233a',
            color: '#FFD447',
            border: '1px solid #FFD447',
            borderRadius: 6
          }}
        >
          <option value="">
            {systems.length === 0 && !loading ? 'No systems available' : 'Select System'}
          </option>
          {systems.map(system => (
            <option key={system} value={system}>{system}</option>
          ))}
        </select>
      </div>

      {/* Planet Dropdown - Only show if system selected and planets available */}
      {selectedSystem && (
        <div className="mb-3">
          <label className="form-label" style={{ color: '#00E6E6', fontWeight: 600 }}>
            Planet / Region {planets.length === 0 ? '(Optional)' : ''}
          </label>
          <select
            className="form-select"
            value={selectedPlanet}
            onChange={(e) => setSelectedPlanet(e.target.value)}
            disabled={loading}
            style={{
              background: '#23233a',
              color: '#FFD447',
              border: '1px solid #FFD447',
              borderRadius: 6
            }}
          >
            <option value="">
              {planets.length > 0 ? 'Select Planet' : 'System-wide locations'}
            </option>
            {planets.map(planet => (
              <option key={planet} value={planet}>{planet}</option>
            ))}
          </select>
        </div>
      )}

      {/* Location Dropdown - Show when we have locations */}
      {selectedSystem && locations.length > 0 && (
        <div className="mb-3">
          <label className="form-label" style={{ color: '#00E6E6', fontWeight: 600 }}>
            Location {required && <span style={{ color: '#ff4444' }}>*</span>}
          </label>
          <select
            className="form-select"
            value={selectedLocation}
            onChange={(e) => setSelectedLocation(e.target.value)}
            disabled={loading}
            required={required}
            style={{
              background: '#23233a',
              color: '#FFD447',
              border: '1px solid #FFD447',
              borderRadius: 6
            }}
          >
            <option value="">Select Location</option>
            {locations.map(location => (
              <option key={location.id} value={location.id}>
                {getLocationLabel(location)}
                {getLocationSubtext(location) && ` - ${getLocationSubtext(location)}`}
              </option>
            ))}
          </select>

          {/* Location Details Preview - Show when location is selected */}
          {selectedLocation && locations.find(loc => loc.id === selectedLocation) && (
            <div
              className="mt-2 p-2"
              style={{
                background: '#1a1a2e',
                border: '1px solid #00E6E6',
                borderRadius: 6,
                fontSize: '0.85rem'
              }}
            >
              {(() => {
                const loc = locations.find(l => l.id === selectedLocation);
                return (
                  <div>
                    <div className="mb-1">
                      <strong style={{ color: '#FFD447' }}>{getLocationLabel(loc)}</strong>
                      {loc.location_type && getLocationTypeBadge(loc.location_type)}
                      {loc.security_level && getSecurityBadge(loc.security_level)}
                    </div>

                    {/* Features */}
                    {(loc.has_commodities || loc.has_merchants || loc.has_loot || loc.has_missions) && (
                      <div className="mb-1" style={{ color: '#00E6E6' }}>
                        <strong>Features:</strong>
                        {getFeatureIcons(loc)}
                      </div>
                    )}

                    {/* Special Tags */}
                    {loc.special_tags && loc.special_tags.length > 0 && (
                      <div className="mb-1" style={{ color: '#9370DB' }}>
                        <strong>Tags:</strong> {loc.special_tags.join(', ')}
                      </div>
                    )}

                    {/* Description */}
                    {loc.description && (
                      <div style={{ color: '#aaa', fontSize: '0.8rem', marginTop: 4 }}>
                        {loc.description}
                      </div>
                    )}

                    {/* Threat Level */}
                    {loc.threat_level && loc.threat_level !== 'low' && (
                      <div className="mt-1" style={{ color: '#FF8C00', fontSize: '0.8rem' }}>
                        <strong>Threat Level:</strong> {loc.threat_level.toUpperCase()}
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          )}
        </div>
      )}

      {/* Loading Indicator */}
      {loading && (
        <div className="text-muted small">
          <span className="spinner-border spinner-border-sm me-2" />
          Loading...
        </div>
      )}

      {/* No locations message */}
      {selectedSystem && !loading && locations.length === 0 && !error && (
        <div className="alert alert-warning" style={{ fontSize: '0.85rem' }}>
          <strong>No locations found</strong>
          <br />
          {tradingOnly && 'No trading locations available for this selection. Try removing filters or selecting a different area.'}
          {!tradingOnly && 'No locations found for this selection. Try selecting a different planet or check system-wide locations.'}
        </div>
      )}

      {/* Data Summary - Show count of available options */}
      {!loading && (
        <div className="mt-2" style={{ fontSize: '0.75rem', color: '#888' }}>
          {systems.length > 0 && <span>{systems.length} system{systems.length !== 1 ? 's' : ''} available</span>}
          {selectedSystem && planets.length > 0 && <span className="ms-2">• {planets.length} planet{planets.length !== 1 ? 's' : ''} in {selectedSystem}</span>}
          {selectedSystem && locations.length > 0 && (
            <span className="ms-2">• {locations.length} location{locations.length !== 1 ? 's' : ''} found</span>
          )}
        </div>
      )}
    </div>
  );
};

export default LocationCascadeSelector;
